import { z } from "zod";
import type {
    CarParkAndRidePreference,
    CarPtAccessMode,
    CarUsageMode,
} from "../../../types/GlobalContext";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

type CarPrefsInput = {
    "carUsageMode":          CarUsageMode;
    "ptAccessModes":         readonly CarPtAccessMode[];
    "carpoolingEnabled":     boolean;
    "parkAndRidePreference": CarParkAndRidePreference;
};

/**
 * Normalizes car prefs after parse (PT access only applies with a personal car).
 *
 * @param params Raw fields before coupling rules.
 * @returns Coherent car preference object for routing and UI.
 */
const normalizeCarPrefs = ( params: CarPrefsInput ): CarPrefsInput => {
    return {
        ...params,
        "ptAccessModes": params.carUsageMode === "personal_car" ? params.ptAccessModes : [],
    };
};

const CarPtAccessModeSchema = z.enum( [ "train", "parking" ] );

const ParkAndRidePreferenceSchema = z.enum( [ "ALL", "MAX_TRANSPORT", "MAX_CAR", "BALANCED" ] );

/** Zod schema for private-car planner preferences. */
const CarPrefsSchema = z.object( {
    "carUsageMode":          z.enum( [ "no_car", "personal_car" ] ).default( "no_car" ),
    "ptAccessModes":         z.array( CarPtAccessModeSchema ).default( [] ),
    "carpoolingEnabled":     z.boolean().default( true ),
    "parkAndRidePreference": ParkAndRidePreferenceSchema.default( "ALL" ),
} ).transform( normalizeCarPrefs );

/** In-memory and persisted shape for car preferences. */
export type CarPrefs = z.infer<typeof CarPrefsSchema>;

/** Parsed defaults used before hydration and as decode fallback. */
export const CAR_PREFS_DEFAULT: CarPrefs = CarPrefsSchema.parse( {} );

/**
 * Maps legacy single `ptAccess` to the v2 `ptAccessModes` array.
 *
 * @param legacyPtAccess Stored v1 access mode.
 * @returns Equivalent v2 mode list.
 */
const legacyPtAccessToModes = ( legacyPtAccess: unknown ): CarPtAccessMode[] => {
    if ( legacyPtAccess === "train" ) {
        return [ "train" ];
    }

    if ( legacyPtAccess === "parking" ) {
        return [ "parking" ];
    }

    return [];
};

/**
 * Ensures v3 payloads expose a park-and-ride preference (defaults to `ALL`).
 *
 * @param previous Raw slice data from version 2.
 * @returns Slice data with `parkAndRidePreference` set when missing.
 */
const migrateCarPrefsV2ToV3 = ( previous: unknown ): unknown => {
    if ( previous === null || typeof previous !== "object" ) {
        return previous;
    }

    const legacy = previous as { "parkAndRidePreference"?: unknown };

    if ( typeof legacy.parkAndRidePreference === "string" ) {
        return previous;
    }

    return {
        ...legacy,
        "parkAndRidePreference": "ALL",
    };
};

/** Persisted slice: car usage mode and public-transport access when driving. */
export const carPrefsSlice = definePersistedSlice<CarPrefs>( {
    "key":            "carPrefs",
    "currentVersion": 3,
    "storage":        "preferences",
    "eager":          true,
    "schema":         CarPrefsSchema,
    "default":        () => CAR_PREFS_DEFAULT,
    "migrations":     {
        "2": ( previous: unknown ): unknown => {
            if ( previous === null || typeof previous !== "object" ) {
                return previous;
            }

            const legacy = previous as {
                "ptAccess"?:      unknown;
                "ptAccessModes"?: unknown;
            };

            if ( Array.isArray( legacy.ptAccessModes ) ) {
                return previous;
            }

            const { ptAccess, ...rest } = legacy;

            return {
                ...rest,
                "ptAccessModes": legacyPtAccessToModes( ptAccess ),
            };
        },
        "3": migrateCarPrefsV2ToV3,
    },
} );
